import React, { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

type AvatarStackProps = {
  avatars?: string[];
  countNumber?: number; // e.g. 94
  prefix?: string; // e.g. "Loved by"
  suffix?: string; // e.g. "users"
  className?: string;
  size?: "sm" | "md";
  ringColorClass?: string; // e.g. "ring-black" or "ring-white/10"
  label?: string; // if provided, show this label and hide numeric count
};

const defaultAvatars = [
  "https://i.pravatar.cc/64?img=1",
  "https://i.pravatar.cc/64?img=2",
  "https://i.pravatar.cc/64?img=3",
  "https://i.pravatar.cc/64?img=4",
  "https://i.pravatar.cc/64?img=5",
  "https://i.pravatar.cc/64?img=6",
  "https://i.pravatar.cc/64?img=7",
  "https://i.pravatar.cc/64?img=8",
];

function formatNumber(n: number) {
  try {
    return new Intl.NumberFormat().format(n);
  } catch {
    return String(n);
  }
}

export const AvatarStack: React.FC<AvatarStackProps> = ({
  avatars = defaultAvatars,
  countNumber,
  prefix = "Loved by",
  suffix = "users",
  className = "",
  size = "sm",
  ringColorClass = "ring-black",
  label
}) => {
  const { user } = useUser();
  const [apiAvatars, setApiAvatars] = useState<string[] | null>(null);
  const [apiCount, setApiCount] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const sizeClasses =
    size === "md"
      ? { avatar: "h-9 w-9", gap: "-ml-2", ring: "ring-2" }
      : { avatar: "h-8 w-8", gap: "-ml-2", ring: "ring-2" };

  // Env overrides prop if defined (even empty string to disable label)
  const envLabel = (import.meta.env.VITE_AVATAR_LABEL as string | undefined);
  const finalLabel = (envLabel !== undefined) ? envLabel : (label ?? null);

  // Try to fetch curated avatars from serverless endpoint
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/avatars", { headers: { "accept": "application/json" } });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data?.avatars) && data.avatars.length > 0) {
          setApiAvatars(data.avatars.slice(0, 8));
        }
        if (typeof data?.count === "number") {
          setApiCount(data.count);
        }
      } catch {
        // silently fall back
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Use env-provided avatar URLs if present (comma-separated), otherwise the provided avatars
  const envUrls = (import.meta.env.VITE_AVATAR_URLS as string | undefined)
    ?.split(",")
    .map((u) => u.trim())
    .filter(Boolean);

  const preset = (apiAvatars && apiAvatars.length > 0)
    ? apiAvatars
    : (envUrls && envUrls.length > 0 ? envUrls : avatars);

  // If user is signed in, prepend their avatar first
  const sources = (user?.imageUrl ? [user.imageUrl, ...preset] : preset).slice(0, 8);

  // Allow control via env var if provided
  const envCount = Number(import.meta.env.VITE_PUBLIC_USER_COUNT);
  const fallbackCount = Number.isFinite(envCount) && envCount > 0 ? envCount : (countNumber ?? 94);
  const effectiveCount = typeof apiCount === "number" && apiCount > 0 ? apiCount : fallbackCount;
  const display = `${prefix} ${formatNumber(effectiveCount)} ${suffix}`;

  return (
    <div className={className}>
      <div className="flex items-center">
        {sources.map((src, idx) => (
          <div
            key={idx}
            className={`${idx === 0 ? "ml-0" : sizeClasses.gap}`}
            style={{ zIndex: 20 - idx }}
          >
            <Avatar className={`${sizeClasses.avatar} ${sizeClasses.ring} ${ringColorClass} rounded-full`}>
              <AvatarImage src={src} alt={`user-${idx + 1}`} />
              <AvatarFallback className="bg-white/10 text-xs">U</AvatarFallback>
            </Avatar>
          </div>
        ))}
      </div>
      {finalLabel ? (
        <div className="mt-2 text-xs sm:text-sm text-white/70">{finalLabel}</div>
      ) : (
        <div className="mt-2 text-xs sm:text-sm text-white/70">
          <span className="mr-1">{prefix}</span>
          <span className="font-semibold text-white">{formatNumber(effectiveCount)}</span>
          <span className="ml-1">{suffix}</span>
        </div>
      )}
    </div>
  );
};

export default AvatarStack;
